/**
 * El marco de las ventanas acopladas. El contenido lo pone [WaChatEmbebido]; acá solo se decide
 * dónde van, cuáles se ven y cómo se minimizan.
 *
 * 🪤 TODAS LAS VENTANAS SE RENDERIZAN SIEMPRE — LAS MINIMIZADAS SE ESCONDEN CON CSS. Es la regla
 * que sostiene el patrón entero: minimizar tiene que conservar el borrador a medio escribir y la
 * posición del scroll. Si acá se hiciera `minimizado ? null : <ventana/>`, React desmontaría el
 * compositor y el operador perdería lo que estaba tipeando cada vez que mira otra cosa — y dejaría
 * de usar el dock a los dos días. El tope de tres ventanas de [WaChatDockContext] existe justamente
 * para que mantenerlas vivas salga barato.
 *
 * 🪤 EL RINCÓN DE ABAJO A LA DERECHA YA ESTÁ OCUPADO. Ahí viven el FAB del copiloto, el del
 * marcador y la tarjeta de llamada activa. El dock arranca más a la izquierda (`RIGHT`) y crece
 * hacia ese lado, así no pelea con ninguno.
 *
 * 🪤 SE RENDERIZA POR PORTAL AL BODY. Adentro del Frame de Polaris, `position: fixed` se ancla al
 * contenedor y no a la ventana. Es el mismo motivo por el que FloatingDialerWidget hace portal.
 * Pero el componente se MONTA dentro del árbol del admin, porque necesita el toast y la sesión.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useBreakpoints } from '@/lib/polaris';
import { useAuth } from '@smarty/shared';
import { useWaChatDock, Z_DOCK, type ChatAcoplado } from '@/contexts/WaChatDockContext';
import { WaChatEmbebido } from '@/components/whatsapp/WaChatEmbebido';
import { ChatDePrueba } from '@/components/agents/ChatDePrueba';
import { WaAvatar } from '@/components/whatsapp/WaAvatar';
import { WaCallNowButton } from '@/components/whatsapp/WaCallNowButton';
import { AppIconButton } from '@/components/admin/AppIconButton';
import { MinusIcon, XIcon, RefreshIcon } from '@/lib/polaris-icons';
import { getAvatarColor, getInitials } from '@/components/whatsapp/wa-helpers';
import {
  ABAJO_ACOPLADA, ALTO_ACOPLADA, ANCHO_ACOPLADA, DERECHA_ACOPLADA, GAP_ACOPLADA, VERDE_ACOPLADA,
} from '@/components/admin/geometriaAcoplada';

const ANCHO = ANCHO_ACOPLADA;
/*
 * Alto de la ventana. Se mide contra la pantalla y no en píxeles fijos.
 *
 * El tope existe para que la ventana no compita con la pantalla de atrás: el dock acompaña el
 * trabajo, no lo reemplaza. 460 era muy poco —se veían cuatro mensajes— y 640 llegaba casi de
 * borde a borde, que es lo mismo que un modal con otra forma. 560 muestra una conversación
 * cómoda dejando ver la lista de pedidos detrás.
 *
 * Los 160px que se restan son el encabezado más el aire de arriba y de abajo, para que en una
 * laptop la ventana tampoco toque los bordes.
 */
const ALTO = ALTO_ACOPLADA;
const GAP = GAP_ACOPLADA;
/** Deja libre la esquina donde ya viven el FAB del copiloto y el del marcador. */
const RIGHT = DERECHA_ACOPLADA;
const BOTTOM = ABAJO_ACOPLADA;
// La capa sale del contexto: el menú de chats del encabezado deriva la suya de la misma constante
// para quedar siempre por encima de estas ventanas.
const Z = Z_DOCK;

/** En la bandeja de Chats el dock sobra: ahí la conversación ya es la pantalla. */
const OCULTO_EN = ['/chats/inbox', '/apps/crm/inbox'];

const VERDE = VERDE_ACOPLADA;

/**
 * 🪤 LA FOTO VA ACÁ Y NO EN CADA MENSAJE. En un chat 1 a 1 WhatsApp no repite el avatar burbuja por
 * burbuja —solo lo hace en grupos, para distinguir quién habla—, y el hilo ya se comporta así. En
 * la cabecera sí corresponde: es lo que te dice a quién le estás escribiendo cuando hay tres
 * ventanas abiertas. Messenger hace exactamente esto.
 *
 * Usa `WaAvatar`, el mismo del resto del módulo, así hereda gratis lo que ya resolvió: si la foto
 * no carga —y las de WhatsApp caducan— cae a las iniciales sobre un color derivado del nombre, en
 * vez de dejar un cuadro roto.
 */
function Cabecera({ chat, foto, alFrente, onMinimizar, onCerrar, onReiniciar }: {
  chat: ChatAcoplado;
  foto: string | null;
  alFrente: boolean;
  onMinimizar: () => void;
  onCerrar: () => void;
  /** Solo en las de prueba: vaciar la conversación sin cerrar la ventana. */
  onReiniciar?: () => void;
}) {
  return (
    <div
      onClick={alFrente ? onMinimizar : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px', background: VERDE, color: '#fff',
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      {/*
        * 🪤 LA PRUEBA LLEVA UN ROBOT, NO LAS INICIALES DEL AGENTE. Sofía tiene nombre de persona: con
        * iniciales sobre un color, su ventana se ve idéntica a la de una clienta llamada Sofía. El
        * robot dice de un vistazo que ese chat no es con nadie real — que es justo lo que hay que
        * poder distinguir cuando quedan tres ventanas abiertas al lado.
        */}
      {chat.tipo === 'prueba' ? (
        <div
          aria-hidden="true"
          style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,255,255,.22)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 15,
          }}
        >
          🤖
        </div>
      ) : (
        <WaAvatar
          size={30}
          src={foto}
          initials={getInitials(chat.nombre, chat.telefono)}
          bg={getAvatarColor(chat.nombre)}
          fontSize={12}
          alt={chat.nombre}
        />
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {chat.nombre}
        </div>
        {chat.etiqueta && (
          <div style={{ fontSize: 11, opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {chat.etiqueta}
          </div>
        )}
      </div>
      {/*
        * Los tres son íconos de la MISMA caja. Antes el guion era un <span> de 12px y la equis el
        * carácter "×" a 15: un dibujo contra una letra, que se alinea por la métrica de la
        * tipografía y nunca queda a eje. Ver AppIconButton.
        *
        * Llamar va primero —como en Messenger— porque es la acción, y minimizar/cerrar son manejo
        * de la ventana: no conviene que la acción quede pegada al botón de cerrar.
        *
        * 🪤 NO ES UN BOTÓN NUEVO. `WaCallNowButton` ya existe y es el que sabe iniciar la llamada
        * (valida el número, evita duplicar una llamada en curso, avisa el resultado); acá se usa
        * su variante `chrome`, que es el mismo componente con otro vestido.
        */}
      {/*
        * 🪤 EN UNA PRUEBA NO HAY A QUIÉN LLAMAR: el botón de llamar se reemplaza por el de reiniciar,
        * que es la acción propia de esta ventana. Y va en la cabecera, no al pie: en 340px de ancho un
        * enlace abajo compite con el campo de escribir y se lee como parte de la conversación.
        */}
      {chat.tipo === 'prueba' ? (
        onReiniciar ? (
          <AppIconButton
            icon={RefreshIcon}
            accessibilityLabel="Reiniciar la conversación de prueba"
            title="Reiniciar"
            onClick={onReiniciar}
            detenerPropagacion
          />
        ) : null
      ) : (
        <WaCallNowButton contactPhone={chat.telefono} contactName={chat.nombre} variant="chrome" />
      )}
      <AppIconButton
        icon={MinusIcon}
        accessibilityLabel={`Minimizar el chat con ${chat.nombre}`}
        onClick={onMinimizar}
        detenerPropagacion
      />
      <AppIconButton
        icon={XIcon}
        accessibilityLabel={`Cerrar el chat con ${chat.nombre}`}
        onClick={onCerrar}
        detenerPropagacion
      />
    </div>
  );
}

export function WaChatDock() {
  const { chats, cerrar, alternarMinimizado, reiniciarPrueba } = useWaChatDock();
  const { tenantId } = useAuth();
  const { pathname } = useLocation();
  const { mdDown: esMobile } = useBreakpoints();
  /*
   * Las fotos las reporta cada chat al resolver su conversación. Se guardan acá, en el marco,
   * porque la cabecera y la píldora son del marco — y así no hay una segunda consulta por la
   * misma fila que el chat ya leyó.
   */
  const [fotos, setFotos] = useState<Record<string, string | null>>({});

  if (!tenantId || chats.length === 0) return null;
  if (OCULTO_EN.some((r) => pathname.includes(r))) return null;

  const expandidos = chats.filter((c) => !c.minimizado);
  const minimizados = chats.filter((c) => c.minimizado);

  /*
   * 🪤 EN CELULAR NO HAY DOCK. Una ventana flotante de 340px sobre una pantalla de 360px no es
   * una ventana: es la pantalla, mal puesta. Ahí se muestra el chat más reciente a pantalla
   * completa —lo mismo que hace Facebook— y el resto queda en la fila de píldoras.
   */
  const visiblesComoVentana = esMobile ? expandidos.slice(-1) : expandidos;
  const comoPildora = esMobile
    ? [...minimizados, ...expandidos.slice(0, -1)]
    : minimizados;

  const dock = (
    <div style={{
      position: 'fixed',
      right: esMobile ? 0 : RIGHT,
      bottom: esMobile ? 0 : BOTTOM,
      left: esMobile ? 0 : undefined,
      top: esMobile ? 0 : undefined,
      zIndex: Z,
      display: 'flex', alignItems: 'flex-end', gap: GAP,
      pointerEvents: 'none',
    }}>
      {/* Píldoras: nombre + contexto. Un avatar solo, como las burbujas de Facebook, no le dice
          nada a quien maneja pedidos — necesita saber DE QUÉ pedido es el chat. */}
      {comoPildora.length > 0 && (
        <div style={{
          display: 'flex', flexDirection: esMobile ? 'row' : 'column', gap: 8,
          pointerEvents: 'auto',
          ...(esMobile ? { position: 'absolute', bottom: 8, left: 8, right: 8, overflowX: 'auto' } : {}),
        }}>
          {comoPildora.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => alternarMinimizado(c.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                maxWidth: 200, padding: '6px 12px',
                background: VERDE, color: '#fff', border: 'none', borderRadius: 999,
                boxShadow: '0 2px 8px rgba(0,0,0,.25)', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              }}
            >
              <WaAvatar
                size={20}
                src={fotos[c.id] ?? null}
                initials={getInitials(c.nombre, c.telefono)}
                bg={getAvatarColor(c.nombre)}
                fontSize={9}
                alt={c.nombre}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.nombre}{c.etiqueta ? ` · ${c.etiqueta}` : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {/*
        * Acá está la regla: se recorren TODOS los chats, no solo los visibles. Los que no se ven
        * quedan con display:none — montados, con su borrador intacto.
        */}
      {chats.map((c) => {
        const visible = visiblesComoVentana.some((v) => v.id === c.id);
        return (
          <div
            key={c.id}
            style={{
              display: visible ? 'flex' : 'none',
              flexDirection: 'column',
              pointerEvents: 'auto',
              width: esMobile ? '100%' : ANCHO,
              height: esMobile ? '100%' : ALTO,
              background: '#fff',
              borderRadius: esMobile ? 0 : '10px 10px 0 0',
              boxShadow: '0 4px 20px rgba(0,0,0,.3)',
              overflow: 'hidden',
            }}
          >
            <Cabecera
              chat={c}
              foto={fotos[c.id] ?? null}
              alFrente={visible}
              onMinimizar={() => alternarMinimizado(c.id)}
              onCerrar={() => cerrar(c.id)}
              onReiniciar={c.tipo === 'prueba' ? () => reiniciarPrueba(c.id) : undefined}
            />
            {/*
              * 🪤 DOS CONTENIDOS, UN SOLO DOCK. La prueba del agente antes tenía su propio portal en
              * esta misma esquina y se dibujaba ENCIMA de los chats reales. Manejarla desde acá le da
              * gratis el acomodo lateral, el tope de tres abiertas y la píldora al minimizar — y evita
              * dos sistemas de ventanas que habría que mantener sincronizados.
              */}
            {c.tipo === 'prueba' ? (
              <ChatDePrueba
                /* El contador de reinicios en la clave remonta el componente: se van juntos la
                   conversación, el borrador y el error. Vaciarlos a mano es olvidarse de uno. */
                key={`prueba-${c.reinicios ?? 0}`}
                tenantId={tenantId}
                agenteId={c.agenteId ?? ''}
                canal={c.canal ?? 'whatsapp'}
              />
            ) : (
              <WaChatEmbebido
                tenantId={tenantId}
                telefono={c.telefono}
                nombreCliente={c.nombre}
                onContactoResuelto={({ fotoUrl }) => setFotos((p) => (p[c.id] === fotoUrl ? p : { ...p, [c.id]: fotoUrl }))}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  return createPortal(dock, document.body);
}
